import { defaultFilterValue } from '@op/components/FilterCommon/constants';
import { isSameTime } from '@op/utils/time';
import _ from 'lodash-es';
import DBO_FWC from '@op/hooks/useFrontWebConfig';
import { channelSplitFlag } from '@op/components/FilterCommon/constants/channel';

const isSetEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every((x) => b.has(x));

// 上报值/配置值 → 「裸渠道名小写」（兼容带/不带 #_# 前缀、原样大小写）
const toBareLower = (v: string) => {
  const s = String(v);
  return (s.includes(channelSplitFlag)
    ? s.split(channelSplitFlag)[1]
    : s
  ).toLocaleLowerCase();
};

// 返回 true = 不同（已改变），与 is_valid / keywords_list_mode 同语义
function makeChannelCompare(
  channelKey: 'social' | 'comments' | 'news' | 'security',
) {
  return ({
    currentValue,
    condInfo,
    data,
  }: {
    currentValue: any;
    condInfo: any;
    data: any;
  }) => {
    // 1. 全集：condInfo.channel[channelKey]（裸名、原样大小写）→ 小写 Set
    const universe: Set<string> = new Set(
      (condInfo?.channel?.[channelKey] || []).map(toBareLower),
    );

    // 2. 默认排除集（全局配置，裸名）→ 小写 Set
    const grouped = DBO_FWC.get('default_no_selected_channels') || {};
    const excludeSet = new Set(
      (Array.isArray(grouped[channelKey]) ? grouped[channelKey] : []).map(
        toBareLower,
      ),
    );

    // 3. 当前选中集（裸名小写），覆盖 4 形态
    const arr = Array.isArray(currentValue) ? currentValue : [];
    const hasNewsExclude =
      channelKey === 'news' &&
      Array.isArray(data?.exclude_channels) &&
      data.exclude_channels.length > 0;

    let selected: Set<string>;
    if (hasNewsExclude) {
      // news 过半：channel_news 被清空，真实选中 = 全集 减去 exclude_channels
      const excl = new Set(data.exclude_channels.map(toBareLower));
      selected = new Set([...universe].filter((c) => !excl.has(c)));
    } else if (arr.length === 1 && arr[0] === '-99999') {
      selected = new Set(); // 全不选
    } else if (arr.length === 0) {
      selected = new Set(universe); // 全选
    } else {
      selected = new Set(arr.map(toBareLower)); // 正向列表
    }
    // 4. 默认选中集 = 全集 减去 配置排除；不等 → true（不同）
    const defaultSelected = new Set(
      [...universe].filter((c) => !excludeSet.has(c)),
    );
    return !isSetEqual(selected, defaultSelected);
  };
}

/**
 * 返回true表示不同
 * 返回false表示相同
 */
const specialFilterCompare: {
  [key: string]: ({
    currentValue,
    defaultValue,
    condInfo,
    edition_unified_id,
    data,
  }: {
    currentValue: any;
    defaultValue: any;
    condInfo: any;
    edition_unified_id: string;
    data: any;
  }) => boolean;
} = {
  is_valid: ({ currentValue, defaultValue }) => {
    return !_.isEqual(_.sortBy(currentValue), _.sortBy(defaultValue));
  },
  version_times: ({ currentValue, condInfo }) => {
    return currentValue?.length !== condInfo?.version?.length;
  },
  social_media_by_account: ({ currentValue, condInfo }) => {
    const accounts = condInfo?.monitor_by_account_category?.monitory || {};
    const keyList = Object.keys(accounts);
    const isUncheck = keyList.some((key: any) => {
      if (key !== 'external-data-sources') {
        const item = accounts[key];
        return item.some((account: any) => account.visibility === 'uncheck');
      }
      return false;
    });

    let allData = ['99999'];
    if (isUncheck) allData = ['public'];
    return !_.isEqual(currentValue, allData);
  },
  social_media_by_keyword: ({ currentValue, condInfo }) => {
    const uncheck = condInfo?.monitor_by_keyword?.uncheck_monitoring || [];
    let allData = ['99999'];
    if (uncheck?.length > 0) allData = ['public'];
    return !_.isEqual(currentValue, allData);
  },
  external_data_sources: ({ currentValue, condInfo }) => {
    const accounts = condInfo?.monitor_by_account_category?.monitory || {};
    const keyList = Object.keys(accounts);
    const isUncheck = keyList.some((key: any) => {
      if (key === 'external-data-sources') {
        const item = accounts[key];
        return item.some((account: any) => account.visibility === 'uncheck');
      }
      return false;
    });

    let allData = ['99999'];
    if (isUncheck) allData = ['public'];
    return !_.isEqual(currentValue, allData);
  },
  keywords_list_mode: ({ currentValue }) => {
    // Include 模式返回 true（表示未改变），Exclude 模式返回 false（表示已改变）
    // 兼容旧数据：无该字段时默认为 include
    return !(currentValue === 'include' || !currentValue);
  },
  // 渠道默认排除方案：保持默认（排除配置项）态不应被误判为"已改变"。
  // 注册键跟 otherInfo 走（security 带 s），参数跟全集/配置走（不带 s）。
  channel_social: makeChannelCompare('social'),
  channel_comments: makeChannelCompare('comments'),
  channel_news: makeChannelCompare('news'),
  channel_securitys: makeChannelCompare('security'),
};

/**
 *
 * @param filter
 * @param condInfo
 * @returns
 *    false: 改变
 *    true: 未改变
 */
export function compareFilterChange(
  filter: any,
  condInfo: any,
  defaultValue?: any,
) {
  if (!filter) return false;
  const {
    date_type,
    end_time,
    start_time,
    edition_unified_id,
    id_type,
    entity_type,
    ...otherInfo
  } = filter;

  if (date_type !== 'daily') return false;

  const isSame = isSameTime(date_type)(start_time, end_time);
  if (!isSame) return false;

  const filterDefaultValue: any = defaultFilterValue(defaultValue);

  const keyList = Object.keys(otherInfo);

  for (const key of keyList) {
    const defaultValue = filterDefaultValue[key];
    const currentValue = otherInfo[key];

    console.log(`【${key}】:`);
    console.log(`  默认值:`, defaultValue);
    console.log(`  当前值:`, currentValue);

    const specialFunc = specialFilterCompare[key];
    if (specialFunc) {
      const flag = specialFunc({
        currentValue,
        defaultValue,
        condInfo,
        edition_unified_id,
        data: otherInfo,
      });
      console.log(`  是否相同:`, flag);
      if (flag) {
        return false;
      }
    } else if (typeof defaultValue === 'object') {
      if (JSON.stringify(defaultValue) !== JSON.stringify(currentValue)) {
        return false;
      }
    } else if ((defaultValue === '-' ? '' : defaultValue) !== currentValue)
      return false;
  }

  return true;
}
